import { useTranslation } from 'react-i18next'

import type { BadgeProps } from '@enechain/ecloud-designsystem'
import { chakra } from '@enechain/ecloud-designsystem'
import { FaClockRegular } from '@enechain/ecloud-icons'

import { Badge } from '~/components/common/Badge'
import { iconSize } from '~/constants/iconSize'
import { formatGcRemainingTime } from '~/models/gcRemainingTime'
import { useSpotDeadlineCountdown } from '~/pages/SpotBidding/hooks/useSpotDeadlineCountdown'
import type { SpotMarketPhase } from '~/pages/SpotBidding/models/marketPhase'
import { spotAcceptanceOpenAtLabel } from '~/pages/SpotBidding/models/marketPhase'

type Props = {
  phase: SpotMarketPhase
  /** 受渡日 (= 入札締切 受渡日前日 10:00 JST の起点)。 */
  targetDate: Date
}

/**
 * 市場フェーズ → 状態バッジ表示仕様。
 *
 * 受付中は「締切までに提出する」 という人の対応を待つ状態なので `warning`
 * (design `spot.html` の `.badge-warn`)。 約定処理中は結果待ちの進行中なので `info`、
 * 約定済は札の結果が確定した望ましい終端なので `positive`、 受渡完了は
 * 良否を持たないので `neutral`。 予約入札は札を作って受付開始を待つ進行中の状態なので
 * `info` — 操作不可を意味する `neutral` ではない (design `spot.html` の予約入札も
 * `.badge-info`。 予約入札フェーズでも作成・編集・削除はできる = AC-BSP-092)。
 *
 * emotion が Badge の配色を 1 つの hash class にまとめるため、 描画された色から
 * この写像を読み戻すことはできない。 写像そのものは値として test で固定する
 * (`SpotPhaseBadge.test.tsx`。 [[SPOT_BID_STATUS_CATEGORY_BADGE]] と同じ作法)。
 */
export const SPOT_PHASE_BADGE: Record<
  SpotMarketPhase,
  { status: BadgeProps['status']; labelKey: string }
> = {
  reserved: { status: 'info', labelKey: 'page:biddingSpot.phase.reserved' },
  open: { status: 'warning', labelKey: 'page:biddingSpot.phase.open' },
  matching: {
    status: 'info',
    labelKey: 'page:biddingSpot.phase.matching',
  },
  matched: {
    status: 'positive',
    labelKey: 'page:biddingSpot.phase.matched',
  },
  completed: {
    status: 'neutral',
    labelKey: 'page:biddingSpot.phase.completed',
  },
}

/**
 * 残り時間を強調表示に切り替える閾値 (design `spot.html` の `badge-error` 分岐 =
 * 残り 30 分未満)。 mock-app (`JepxSpotBid.razor`) は 60 分で振るが、
 * visual は design を正とし、design 側を採る。
 */
const URGENT_THRESHOLD_SECONDS = 30 * 60

/**
 * 受付中のカウントダウン中の Badge の status。 残り 30 分未満はバッジ全体を `negative`
 * (DS の赤。 design の `.badge-error`) に振り、 それ以外は通常の `warning`。
 * UI 辞書 `countdown.md`「スポットの状態バッジ」 / `spot.html` の
 * `(h === 0 && m < 30) ? 'badge-error' : 'badge-warn'` と同じ (2026-10-27 ユーザー確認)。
 */
export const countdownBadgeStatus = (
  remainingSeconds: number,
): BadgeProps['status'] =>
  remainingSeconds < URGENT_THRESHOLD_SECONDS ? 'negative' : 'warning'

/**
 * page-header 右端の市場フェーズバッジ。 受付中フェーズでは入札締切 (受渡日前日 10:00)
 * までの残り時間をカウントダウン表示する (spec AC-BSP-044 [P0])。
 *
 * スポットは前日 10:00 の締切直前に提出する業務が発生するため、 残り時間の常時可視化が
 * 締切間際の業務判断を支える (spec `spot-bidding.md` §業務制約)。 バッジは page-header に
 * 1 つだけ置かれ、 通常入札タブ・ブロック入札タブの両方から見える (両者は同じ
 * 前日 10:00 締切を共有するため、 タブごとに別のカウントダウンは持たない)。
 * 時間前入札は受渡コマごとに GC が異なるので、 専用の `GcCountdown` を持ち本 component は
 * 使わない。
 *
 * design HTML: `design/bidding/spot.html` の `#spot-state-badge`
 * (`.badge.badge--countdown` = gap 4px + tabular-nums + 先頭に時計 icon)。
 * 残り 30 分未満はバッジ全体を `negative` (赤) に振る ([[countdownBadgeStatus]]。
 * design `.badge-error`、 UI 辞書 `countdown.md`)。 以前は文字だけを negative にしていたが、
 * 辞書と 8080 はバッジ全体を赤にするので揃えた (2026-10-27)。 パルスは付けない
 * (「残りわずか」 の点滅は `GcCountdown` の `.gc-urgent` に一本化)。
 *
 * 残り時間の表記は canonical な [[formatGcRemainingTime]] (= `HH:MM:SS`) に揃える。
 * design HTML は残り時間に応じた段階粒度 (「N日M時間」 / 「H時間M分」 / 「M分S秒」 /
 * 「M:SS」 / 「N秒」) を組んでいるが、 カウントダウン表記は画面をまたいで同じ桁に
 * 揃える裁定が後から出ている (issue #4858、 デザイナー裁定 2026-08-14。 時間前入札と
 * 需要想定は既に `HH:MM:SS`)。 受付中は spec どおり受渡日 11 日前 0:00 から始まる
 * (AC-BSP-091、 issue #7021) ので残り時間は最長で約 250 時間になる。
 * この 24 時間超のレンジの表記は issue #8320 で決着済み: 記録済みの PdM 判断
 * (issue #4855 2026-08-18 → spec `intraday-bidding.md` AC-BIN-001) が定める
 * 「24 時間以上は `N日 HH:MM:SS`」 に揃える。 これは canonical な
 * [[formatGcRemainingTime]] 本体が持つ責務なので (PR #7999)、 本 component は
 * formatter を呼ぶだけでよく、 画面側に日跨ぎの分岐を持たない
 * (Des 確定 2026-09-18、 issue #8320)。 承認申請確認 modal だけは 1 回きりの意思決定の
 * 場なので締切日時 (絶対時刻) を出す ([[formatSpotDeadlineDisplay]]) — 常設バッジとは
 * 役割が違うため揃えない、 も同確定に含まれる。
 */
export const SpotPhaseBadge = ({
  phase,
  targetDate,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const { status, labelKey } = SPOT_PHASE_BADGE[phase]
  const remainingSeconds = useSpotDeadlineCountdown(
    targetDate,
    phase === 'open',
  )
  // 予約入札だけはラベルに受付開始日時を差し込む (spec §市場フェーズ の
  // 「予約入札 (受付開始 M/D HH:MM〜)」)。 いつから出せるのかがバッジ単体で読めないと、
  // 受渡日を先に送った利用者が「まだ入札できない」 としか分からない。
  const label =
    phase === 'reserved'
      ? t(labelKey, {
          openAt: spotAcceptanceOpenAtLabel(targetDate),
        })
      : t(labelKey)

  if (remainingSeconds === undefined) {
    return <Badge status={status}>{label}</Badge>
  }

  return (
    <Badge
      gap={1}
      css={{ fontVariantNumeric: 'tabular-nums' }}
      status={countdownBadgeStatus(remainingSeconds)}
    >
      <FaClockRegular
        flexShrink={0}
        boxSize={iconSize.badgeInline}
        aria-hidden="true"
      />
      {/* ラベルと残時間は別 span に分ける (design も `<span>` でラベルを包む)。
          test / e2e からもフェーズラベル単独で到達できるようにする。 */}
      <chakra.span>{label}</chakra.span>
      <chakra.span>
        {t('page:biddingSpot.phase.openRemaining', {
          time: formatGcRemainingTime(remainingSeconds),
        })}
      </chakra.span>
    </Badge>
  )
}
